import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { type ReactNode, useEffect } from "react";

import { getCachedWhoAmI, getToken, logout } from "@/lib/ddb";
import { useHydrateCabinetClasses } from "@/lib/cabinets/dsl/registry";
import { CabinetClassesPage } from "@/pages/CabinetClassesPage";
import { LoginPage } from "@/pages/LoginPage";
import { ProblemsPage } from "@/pages/ProblemsPage";
import { ProjectsPage } from "@/pages/ProjectsPage";
import { ProjectEditorPage } from "@/pages/ProjectEditorPage";
import { BomPanel } from "@/panels/BomPanel";
import { CutListPanel } from "@/panels/CutListPanel";
import { ExportPanel } from "@/panels/ExportPanel";
import { QuotePanel } from "@/panels/QuotePanel";
import { RoomEditorPanel } from "@/panels/RoomEditorPanel";

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<RequireAuth><Shell /></RequireAuth>}>
        <Route index element={<Navigate to="projects" replace />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:id/design" element={<ProjectEditorPage />} />
        <Route path="projects/:id/bom"    element={<BomPanel />} />
        <Route path="projects/:id/cutlist" element={<CutListPanel />} />
        <Route path="projects/:id/export"  element={<ExportPanel />} />
        <Route path="projects/:id/quotes/:quoteId" element={<QuotePanel />} />
        <Route path="projects/:id/room"    element={<RoomEditorPanel />} />
        <Route path="projects/:id/problems" element={<ProblemsPage />} />
        <Route path="catalog/cabinet-classes" element={<CabinetClassesPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    if (!getToken()) nav("/login", { replace: true, state: { from: loc.pathname } });
  }, [loc.pathname, nav]);

  if (!getToken()) return null;
  return <>{children}</>;
}

function Shell() {
  const nav = useNavigate();
  const who = getCachedWhoAmI();
  // Hydrate the cabinet-class registry from ddb at app boot so pure
  // helpers that call `getCabinetClass(id)` synchronously see the
  // designer-authored classes alongside the embedded seed.
  useHydrateCabinetClasses();

  async function handleSignOut() {
    await logout();
    nav("/login", { replace: true });
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <span className="brand">CABINET STUDIO</span>
        <a href="/projects">Projects</a>
        {(who?.isAdmin || who?.role === "designer") && (
          <a href="/catalog/cabinet-classes">Classes</a>
        )}
        <span className="spacer" />
        {who && <span className="role-chip">{who.role}</span>}
        {who && <span className="muted" style={{ fontSize: 13 }}>{who.username}</span>}
        <button className="ghost" onClick={handleSignOut}>Sign out</button>
      </header>
      <main style={{ overflow: "auto" }}>
        <Outlet />
      </main>
    </div>
  );
}

function NotFound() {
  return <div className="empty-state">Page not found.</div>;
}
